import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronUp,
  BarChart3,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { cn } from '../../utils/cn'
import { motion, AnimatePresence } from 'framer-motion';
import { ResponsiveContainer } from 'recharts';

interface MobileChartCardProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode; // Chart component
  defaultExpanded?: boolean;
  summary?: {
    label: string;
    value: string | number;
    trend?: 'up' | 'down' | 'neutral';
  }[];
  className?: string;
  onToggle?: (expanded: boolean) => void;
}

export function MobileChartCard({
  title,
  subtitle,
  children,
  defaultExpanded = false,
  summary,
  className,
  onToggle,
}: MobileChartCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // C6 A-6 fix + Sprint C10 Lane D (C6 #27) defaultExpanded gating:
  // shouldRenderChart starts as `false` even when defaultExpanded=true so the
  // ResizeObserver gate also covers the desktop fast-path. Previously this
  // initialized to `defaultExpanded` and bypassed the negative-dimension
  // protection on first mount; recharts would then log the
  // "ResizeObserver loop completed with undelivered notifications" warning
  // (and the prior recharts width(-1)/height(-1) warning) on any first paint
  // where the parent grid had not yet measured. The ResizeObserver effect
  // below flips containerMeasured to true once positive dimensions arrive,
  // and the second effect then mounts the chart -- single code path for
  // both expand-from-collapsed and default-expanded mounts.
  const [shouldRenderChart, setShouldRenderChart] = useState(false);

  // Defer chart mount until the chart container has measured dimensions.
  // The expand animation (height: 0 -> 'auto', 300ms) used to wake recharts
  // at height=0, producing the negative-dimension warning. ResizeObserver
  // flips containerMeasured the first time positive dimensions are observed.
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [containerMeasured, setContainerMeasured] = useState(false);

  useEffect(() => {
    if (!isExpanded) {
      setContainerMeasured(false);
      return;
    }
    const node = chartContainerRef.current;
    if (!node) return;
    if (typeof ResizeObserver === 'undefined') {
      // SSR or very old browsers: fall back to immediate mount
      setContainerMeasured(true);
      return;
    }
    // Wrap the observer setup in requestAnimationFrame so the parent layout
    // has a chance to paint at least one frame before we start measuring.
    // This is the cheaper of the two remediations the C6 #27 follow-on
    // proposed (vs. useLayoutEffect + manual measurement). It is sufficient
    // because the only failure mode is "observe before parent painted".
    const raf = requestAnimationFrame(() => {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            setContainerMeasured(true);
            observer.disconnect();
            break;
          }
        }
      });
      observer.observe(node);
      // Stash the observer on the node so the cleanup can disconnect it.
      (node as HTMLDivElement & { __mccObserver?: ResizeObserver }).__mccObserver = observer;
    });
    return () => {
      cancelAnimationFrame(raf);
      const stash = (node as HTMLDivElement & { __mccObserver?: ResizeObserver }).__mccObserver;
      if (stash) {
        stash.disconnect();
        (node as HTMLDivElement & { __mccObserver?: ResizeObserver }).__mccObserver = undefined;
      }
    };
  }, [isExpanded]);

  useEffect(() => {
    if (isExpanded && !shouldRenderChart && containerMeasured) {
      // Mount chart only AFTER the container has positive measured size,
      // and after the framer-motion expand animation has had a frame to
      // settle (single rAF + microtask is enough).
      const timer = setTimeout(() => setShouldRenderChart(true), 50);
      return () => clearTimeout(timer);
    }
    if (!isExpanded && shouldRenderChart) {
      setShouldRenderChart(false);
    }
  }, [isExpanded, shouldRenderChart, containerMeasured]);

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  const handleFullscreen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsFullscreen(!isFullscreen);
  };

  return (
    <>
      <motion.div
        layout={!isFullscreen}
        className={cn(
          'bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden',
          'transition-all duration-200',
          isFullscreen && 'fixed inset-4 z-50 m-0',
          className
        )}
      >
        {/* Header - Always Visible */}
        <div
          className={cn(
            'w-full p-4',
            'cursor-pointer active:bg-gray-800/50',
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0" onClick={handleToggle}>
              <div className="flex items-center gap-2 mb-1">
                <BarChart3 className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <h3 className="text-base font-semibold text-white truncate">{title}</h3>
              </div>
              {subtitle && (
                <p className="text-sm text-gray-400">{subtitle}</p>
              )}
              
              {/* Summary Stats - Visible when collapsed */}
              {!isExpanded && summary && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  {summary.slice(0, 2).map((item, index) => (
                    <div key={index} className="bg-gray-800/50 rounded-lg p-2">
                      <div className="text-xs text-gray-500">{item.label}</div>
                      <div className="text-sm font-medium text-white flex items-center gap-1">
                        {item.value}
                        {item.trend && (
                          <span className={cn(
                            'text-xs',
                            item.trend === 'up' ? 'text-green-500' : 
                            item.trend === 'down' ? 'text-red-500' : 
                            'text-gray-500'
                          )}>
                            {item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              {isExpanded && (
                <button
                  onClick={handleFullscreen}
                  className="p-1 text-gray-400 hover:text-white rounded transition-colors"
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <Minimize2 className="w-5 h-5" />
                  ) : (
                    <Maximize2 className="w-5 h-5" />
                  )}
                </button>
              )}
              <button
                onClick={handleToggle}
                className="p-1 text-gray-400 hover:text-white rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
              >
                <motion.div
                  animate={{ rotate: isExpanded ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </button>
            </div>
          </div>
        </div>

        {/* Expandable Chart */}
        <AnimatePresence initial={false}>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div
                ref={chartContainerRef}
                className={cn(
                  'px-4 pb-4 border-t border-gray-800/50',
                  isFullscreen ? 'h-[calc(100vh-120px)]' : 'h-64'
                )}
              >
                {shouldRenderChart ? (
                  <div className="w-full h-full">
                    {children}
                  </div>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-gray-500 text-sm">Loading chart...</div>
                  </div>
                )}
              </div>
              
              {/* Extended Summary - Visible when expanded */}
              {summary && summary.length > 0 && (
                <div className="px-4 pb-4">
                  <div className={cn(
                    'grid gap-3',
                    isFullscreen ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'
                  )}>
                    {summary.map((item, index) => (
                      <div key={index} className="bg-gray-800/50 rounded-lg p-3">
                        <div className="text-xs text-gray-500 mb-1">{item.label}</div>
                        <div className="text-base font-semibold text-white flex items-center gap-2">
                          {item.value}
                          {item.trend && (
                            <span className={cn(
                              'text-sm',
                              item.trend === 'up' ? 'text-green-500' : 
                              item.trend === 'down' ? 'text-red-500' : 
                              'text-gray-500'
                            )}>
                              {item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Fullscreen Backdrop */}
      {isFullscreen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 z-40"
          onClick={() => setIsFullscreen(false)}
        />
      )}
    </>
  );
}